import { useState } from "react";
import { Modal, Form, Input, Button, message } from "antd";
import { changePassword } from "../../services/auth.service";

/**
 * ChangePasswordModal — Force password change modal
 * 
 * Shows when user logs in with forceResetOnLogin flag.
 * User must change password before accessing the system.
 * Modal cannot be closed until password is changed.
 */
export default function ChangePasswordModal({ open, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await changePassword({
        currentPassword: values.currentPassword.trim(),
        newPassword: values.newPassword.trim(),
      });
      message.success("Password changed successfully!");
      form.resetFields();
      onSuccess();
    } catch (err) {
      const errorMsg =
        err?.errors?.[0]?.msg || err?.message || "Failed to change password";
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Change Password Required"
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={480}
    >
      <p style={{ marginBottom: 24, color: "#666" }}>
        You must change your temporary password before accessing the system.
      </p>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Current Password"
          name="currentPassword"
          rules={[
            { required: true, message: "Current password is required" },
          ]}
        >
          <Input.Password
            placeholder="Enter temporary password"
            autoComplete="current-password"
          />
        </Form.Item>

        <Form.Item
          label="New Password"
          name="newPassword"
          rules={[
            { required: true, message: "New password is required" },
            { min: 6, message: "Password must be at least 6 characters" },
          ]}
        >
          <Input.Password
            placeholder="Enter new password"
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item
          label="Confirm New Password"
          name="confirmPassword"
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: "Please confirm your new password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Passwords do not match"));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="Confirm new password"
            autoComplete="new-password"
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" block loading={loading}>
            Change Password
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
